import React from 'react';

// ── Inline word-level redline renderer ─────────────────────────────
// Props:
//   result: RedlineResult from POST /api/tax-disclosure/check
//   header: optional string rendered at the top (deal type / state / etc.)

const insStyle = {
  color: '#117a36',
  textDecoration: 'none',
  background: 'rgba(17, 122, 54, 0.10)',
  padding: '0 1px',
  borderRadius: '2px',
};

const delStyle = {
  color: '#a01d1d',
  textDecoration: 'line-through',
  background: 'rgba(160, 29, 29, 0.10)',
  padding: '0 1px',
  borderRadius: '2px',
};

const blockInsStyle = {
  color: '#117a36',
  background: 'rgba(17, 122, 54, 0.08)',
  borderLeft: '3px solid #117a36',
  padding: '8px 12px',
  margin: '0 0 12px 0',
  whiteSpace: 'pre-wrap',
  fontFamily: 'var(--font-body)',
};

const blockDelStyle = {
  color: '#a01d1d',
  background: 'rgba(160, 29, 29, 0.08)',
  borderLeft: '3px solid #a01d1d',
  textDecoration: 'line-through',
  padding: '8px 12px',
  margin: '0 0 12px 0',
  whiteSpace: 'pre-wrap',
  fontFamily: 'var(--font-body)',
};

const paraStyle = {
  margin: '0 0 12px 0',
  lineHeight: '1.6',
  whiteSpace: 'pre-wrap',
  fontFamily: 'var(--font-body)',
  color: 'var(--text-primary)',
};

const splitMarkerStyle = {
  display: 'inline-block',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: '#b87333',
  marginRight: '6px',
  verticalAlign: 'middle',
  title: 'split from multi-paragraph pairing',
};

function renderAlignedSegment(diff, key, split) {
  return (
    <p key={key} style={paraStyle}>
      {split && <span style={splitMarkerStyle} title="split from multi-paragraph pairing" />}
      {diff.map((d, i) => {
        if (d.op === 'equal') return <span key={i}>{d.text}</span>;
        if (d.op === 'insert') return <ins key={i} style={insStyle}>{d.text}</ins>;
        if (d.op === 'delete') return <del key={i} style={delStyle}>{d.text}</del>;
        return null;
      })}
    </p>
  );
}

export default function Redline({ result, header }) {
  if (!result || !Array.isArray(result.segments)) return null;

  return (
    <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
      {header && (
        <div style={{
          fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
          borderBottom: '1px solid var(--border-color)', paddingBottom: '8px',
          marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {header}
        </div>
      )}

      <div style={{
        padding: '20px 24px',
        background: '#ffffff',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}>
        {result.segments.length === 0 && (
          <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            No differences detected.
          </div>
        )}
        {result.segments.map((seg, idx) => {
          const split = Boolean(seg.split_from_pairing);
          if (seg.kind === 'aligned') {
            return renderAlignedSegment(seg.diff || [], idx, split);
          }
          if (seg.kind === 'baseline_only') {
            return (
              <p key={idx} style={blockDelStyle}>
                {split && <span style={splitMarkerStyle} title="split from multi-paragraph pairing" />}
                {seg.baseline_text}
              </p>
            );
          }
          if (seg.kind === 'proposed_only') {
            return (
              <p key={idx} style={blockInsStyle}>
                {split && <span style={splitMarkerStyle} title="split from multi-paragraph pairing" />}
                {seg.proposed_text}
              </p>
            );
          }
          return null;
        })}
      </div>

      <div style={{
        marginTop: '10px', fontSize: '11px', color: 'var(--text-secondary)',
        display: 'flex', gap: '18px', alignItems: 'center',
      }}>
        <span><ins style={insStyle}>green</ins> = added in proposed</span>
        <span><del style={delStyle}>red</del> = in model, removed from proposed</span>
      </div>

      {result.diagnostics && (
        <details style={{
          marginTop: '12px', fontSize: '11px', color: 'var(--text-secondary)',
          border: '1px solid var(--border-color)', borderRadius: '4px',
          padding: '6px 10px', background: 'var(--bg-tertiary)',
        }}>
          <summary style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
            Diagnostics
          </summary>
          <div style={{ marginTop: '8px', fontFamily: 'var(--font-mono, monospace)', lineHeight: '1.5' }}>
            <div>extraction_method: {String(result.diagnostics.extraction_method)}</div>
            <div>extracted_char_count: {String(result.diagnostics.extracted_char_count)}</div>
            <div>baseline_paragraph_count: {String(result.diagnostics.baseline_paragraph_count)}</div>
            <div>proposed_paragraph_count: {String(result.diagnostics.proposed_paragraph_count)}</div>
            <div>alignment_method: {String(result.diagnostics.alignment_method)}</div>
            <div>
              unpaired_baseline_indices:{' '}
              <span style={(result.diagnostics.unpaired_baseline_indices || []).length > 0 ? { color: '#b87333', fontWeight: 600 } : undefined}>
                {JSON.stringify(result.diagnostics.unpaired_baseline_indices || [])}
              </span>
            </div>
            <div>
              unpaired_proposed_indices:{' '}
              <span style={(result.diagnostics.unpaired_proposed_indices || []).length > 0 ? { color: '#b87333', fontWeight: 600 } : undefined}>
                {JSON.stringify(result.diagnostics.unpaired_proposed_indices || [])}
              </span>
            </div>
            <div>multi_pairings_count: {String(result.diagnostics.multi_pairings_count || 0)}</div>
            <div>
              sub_split_count:{' '}
              <span style={(result.diagnostics.sub_split_count || 0) > 0 ? { color: '#b87333', fontWeight: 600 } : undefined}>
                {String(result.diagnostics.sub_split_count || 0)}
              </span>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
